/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable eqeqeq */
/* eslint-disable prettier/prettier */
import { Injectable } from '@angular/core';
import { LenderService } from '@shared/service/lender/lender.service';
import { PROTOCOL_MORTGAGE } from '../../product-helper';
import { JointCreditManager } from '../../types/joint-applicant/joint-credit-manager.class';
import { coBorrowersPath } from '../../types/joint-applicant/applicant-stateful.class';
import { getValue } from '../utilities.service';
import { CoreControlType } from '@workflow/class/workflow.type';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldService } from '@workflow/class/field.service';

const PHONE_NUMBER_RX = /^(1\s?)?((\([0-9]{3}\))|[0-9]{3})[\s\-]?[\0-9]{3}[\s\-]?[0-9]{4}$/;
const SSN_RX = /^[0-9]{3}([ -]?)[0-9]{2}\1[0-9]{4}$/;
const EIN_RX = /^[0-9]{2}([ -]?)[0-9]{7}$/;
/** @deprecated Not used - remove this if planning to use */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const US_ZIP_CODE_RX = /^\d{5}(?:[-\s]\d{4})?$/;
const ALPHA_NUMERIC_RX = /^[a-zA-Z0-9]{5}$/;
const NUMERIC_ONLY_RX = /^[0-9]*$/;
const ALPHANUMERIC_HYPHEN_ONLY_RX = /^[a-zA-Z0-9-]+$/;
const NUMERIC_COMMA_SPACE_RX = /^[\d,\s]+$/;
const LETTER_COMMA_SPACE_RX = /^[a-zA-Z,\s]+$/;
const NAME_SPECIFIC_CHARACTERS_ONLY_RX = /^[a-zA-Z][a-zA-z '-]*$/;
const NAME_SPECIFIC_CHARACTERS_NUMERIC_ONLY_RX = /^[a-zA-Z0-9 ,]*$/;
const NAME_SPECIFIC_CHARACTERS_HYPHEN_APOSTROPHE_ONLY_RX = /^[a-zA-z '-]*$/;
const MAX_PERCENT = 100;

/**
 * This service will be used for custom field or stateful validations
 * Methods on this service get registered as 'logic functions' on LogicService
 */
@Injectable({
  providedIn: 'root'
})
export class FieldValidationFunctionsService {
  constructor(
    private readonly lenderService: LenderService,
    private readonly statefulService: StatefulService,
    private readonly fieldService: FieldService
  ) {}

  /**
   * this validation should be used on a field that has nested fields
   * @param context field context
   */
  public checkboxSelectionRequired = (context: LogicFnContext, minimumNumberOfCheck = 1): boolean => {
    const isChecked = (val) => /^true$/i.test(val);
    const checkboxes = context.field.nestedFields.filter((f) => f.config.type === 'checkbox' && f.visible);

    if (checkboxes.length) {
      return checkboxes.filter((ch) => isChecked(ch.readableValue)).length >= minimumNumberOfCheck;
    }
  };
  /**
   * validation against the phone number
   * @param context field context
   */
  public phoneNumber = (context: LogicFnContext): boolean => {
    const value = getValue(context);
    if (typeof value === 'string') {
      return !value || !!value.match(PHONE_NUMBER_RX);
    }
  };

  /**
   * validate required field
   * @param context field context
   */
  public checkRequired = (context: LogicFnContext): boolean => {
    const isRequired = context.field.required;
    const value = getValue(context);
    return !isRequired || (value && isRequired);
  };

  /**
   * validation against the ssn
   * @param context field context
   */
  public ssn = (context: LogicFnContext): boolean => {
    this.setSecurityValue(context);
    const value = getValue(context);
    if (typeof value === 'string') {
      if (value.startsWith('*')) {
        return !value || value.length === 9;
      }
      return !value || !!value.match(SSN_RX);
    }
  };

  /**
   * validation against the ein
   * @param context field context
   */

  public ein = (context: LogicFnContext): boolean => {
    this.setSecurityValue(context);
    const value: string = context.field.readableValue;
    if (typeof value === 'string') {
      if (value.startsWith('*')) {
        return !value || value.length === 9;
      }
      return !value || !!value.match(EIN_RX);
    }
  };

  /**
   * Memory storage is null When browser refresh/reload, need to set before call validation
   * @param context field context
   */
  public setSecurityValue = (_context: LogicFnContext): void => {
    if (_context.field && _context.field.config.type === ('input-mask' as CoreControlType) && !_context.field.dirty) {
      // eslint-disable-next-line max-len
      // const apiValue: any = this.statefulService.wrap(_context.field.config.storageId.replace('mem:', 'api:'), null, _context.field.pageIterationId)
      //  .value;
      // eslint-disable-next-line max-len
      const apiValue: any = this.statefulService.wrap(
        _context.field.config.storageId.replace('mem:', 'api:'),
        null,
        _context.field.pageIterationId
      ).value;
      // const memValue: any = this.statefulService.wrap(_context.field.config.storageId, null, _context.field.pageIterationId).value;
      const memValue: any = this.statefulService.wrap(_context.field.config.storageId, null, _context.field.pageIterationId).value;
      if (apiValue && !memValue) {
        // this.statefulService.wrap(_context.field.config.storageId, null, _context.field.pageIterationId).value = apiValue;
        this.statefulService.wrap(_context.field.config.storageId, null, _context.field.pageIterationId).value = apiValue;
      }
    }
  };

  /**
   * validation against the ssn
   * @param context field context
   */
  public usZipCode = (context: LogicFnContext): boolean => {
    const value = getValue(context);
    if (typeof value === 'string') {
      return !value || !!value.match(ALPHA_NUMERIC_RX);
    }
  };

  /**
   * validation against the number
   * @param context field context
   */
  public numberOnly = (context: LogicFnContext): boolean => {
    const value = getValue(context);
    if (typeof value === 'string') {
      return !value || !!value.match(NUMERIC_ONLY_RX);
    }
  };

  /**
   * validation against the letter comma and space
   * @param context field context
   */
  public letterWithCommaSpace = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;
    if (value.match(LETTER_COMMA_SPACE_RX) === null) {
      if (!value) {
        return true;
      }
      return false;
    }
    return true;
  };

  /**
   * validation against the number comma and space
   * @param context field context
   */
  public numberWithCommaSpace = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;
    if (typeof value === 'string') {
      return !value || !!value.match(NUMERIC_COMMA_SPACE_RX);
    }
  };

  /**
   * Date validation for age verification
   * @param context field context
   */
  public ageOverEighteen = (context: LogicFnContext): boolean => {
    const now = new Date();
    const contextData = getValue(context);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (contextData === null || contextData === undefined) return true;
    const dob = new Date(contextData.toString());
    if (isNaN(dob.getDate()) || dob > today) {
      return true;
    }
    const legalAge = new Date(dob.getFullYear() + 18, dob.getMonth(), dob.getDate());
    return today >= legalAge;
  };

  /**
   * Date validation for age verification under 18.
   * @param context field context
   */
  public ageUnderEighteen = (context: LogicFnContext): boolean => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dob = new Date(context.field.readableValue);
    if (isNaN(dob.getDate())) {
      return true;
    }
    const legalAge = new Date(dob.getFullYear() + 18, dob.getMonth(), dob.getDate());
    return today < legalAge;
  };
  /**
   * Date validation for Youth Application
   * @param context field context
   */
  public ageVerificationForYouth = (context: LogicFnContext, minAgeId: string, maxAgeId: string): boolean => {
    // const maxAgeYear = this.statefulService.wrap(maxAgeId).valueNumber;
    const maxAgeYear = this.statefulService.wrap(maxAgeId).valueNumber;
    // const minAgeYear = this.statefulService.wrap(minAgeId).valueNumber;
    const minAgeYear = this.statefulService.wrap(minAgeId).valueNumber;
    if (minAgeYear && maxAgeYear) {
      const now = new Date();
      // today's date, but with the time set to midnight. This ensures that only
      // the date part is considered, not the time.
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dob = new Date(context.field.readableValue);
      if (isNaN(dob.getDate())) {
        return false;
      }
      // legalAgeMax is the date when the person would turn 18 years old.
      const legalAgeMax = new Date(dob.getFullYear() + maxAgeYear, dob.getMonth(), dob.getDate());
      // legalAgeMin is the date when the person would turn 13 years old.
      const legalAgeMin = new Date(dob.getFullYear() + minAgeYear, dob.getMonth(), dob.getDate());
      // today's date is after or on the date they turn 13 and before the date they turn 18
      return today >= legalAgeMin && today < legalAgeMax;
    }
  };

  /**
   * Date validation for Current address and previous address
   */
  public PreviousAddressDate = (context: LogicFnContext): boolean => {
    const storageId = context.field.config.storageId;
    // eslint-disable-next-line max-len
    // const currentAddressDateValue = this.statefulService.wrap(storageId, 0).value ? (this.statefulService.wrap(storageId, 0).value as string) : '';
    // eslint-disable-next-line max-len
    const currentAddressDateValue = this.statefulService.wrap(storageId, 0).value
      ? (this.statefulService.wrap(storageId, 0).value as string)
      : '';
    const currentDate = new Date(currentAddressDateValue);
    const prevAddressDateValue = context.field.data.valueStringOnly;
    const prevDate = prevAddressDateValue ? new Date(prevAddressDateValue) : '';
    if (currentAddressDateValue.length < 10 || prevAddressDateValue.length < 10) {
      return true;
    }
    if (prevDate >= currentDate && context.field.iterationId != 0) {
      return false;
    }
    return true;
  };

  /**
   * Email Address validation for Applicant and Co-Applicant
   */
  public UniqueEmailAddress = (context: LogicFnContext, applicant: string): boolean => {
    const value = context.field.readableValue;
    let coBorrowers = this.statefulService.arraySize(coBorrowersPath(PROTOCOL_MORTGAGE)) - 1;

    if (applicant && value && coBorrowers >= 0) {
      const emailList = [];

      if (applicant && context.field.pageIterationId >= 0) {
        emailList.push(applicant.toLowerCase());
      }
      while (coBorrowers >= 0) {
        if (coBorrowers !== context.field.pageIterationId) {
          const storageId = `${coBorrowersPath(PROTOCOL_MORTGAGE)}/#/borrowerInfo/emailAddress`;
          // const emailAddress = this.statefulService.wrap(storageId, null, coBorrowers);
          const emailAddress = this.statefulService.wrap(storageId, null, coBorrowers);
          if (emailAddress && emailAddress.value) {
            emailList.push(emailAddress.value.toString().toLowerCase());
          }
        }
        coBorrowers--;
      }

      if (emailList.includes(value.toLowerCase())) {
        return false;
      }
    }

    return true;
  };

  public validDuration = (context: LogicFnContext, relatedFieldName: string): boolean => {
    if (!context.field.data) {
      return true;
    }
    const contextFieldDuration = context.field.data.valueNumber;
    const pageIterationId = context.field.pageIterationId;
    const relatedfield = this.fieldService.createField(relatedFieldName, context.field.flowId, context.field.iterationId, pageIterationId);
    const relatedFieldDuration = relatedfield.data.valueNumber;
    return contextFieldDuration !== 0 || relatedFieldDuration !== 0 || !context.field.dirty || !relatedfield.dirty;
  };

  public validateSsnLenderRequirement = (context: LogicFnContext): boolean => {
    if (this.lenderService?.lenderData?.isOAOSsnRequired) {
      const validationArray = context.field.config.validation;
      const lastIndex = validationArray.length - 1;
      if (validationArray && lastIndex >= 0 && !validationArray[lastIndex][0].includes('required')) {
        context.field.config.validation = validationArray.concat([['required']]);
      }
    }
    return true;
  };

  /**
   * Validation against name specific strings.
   * This includes spaces, dashes, apostrophes.
   * @param context field context
   */
  public nameTextOnly = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;
    if (typeof value === 'string') {
      return !value || !!value.match(NAME_SPECIFIC_CHARACTERS_ONLY_RX);
    }
  };

  public nameHyphenApostropheOnly = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;
    if (typeof value === 'string') {
      return !value || !!value.match(NAME_SPECIFIC_CHARACTERS_HYPHEN_APOSTROPHE_ONLY_RX);
    }
  };

  public alphaNumberHyphenOnly = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;
    if (typeof value === 'string') {
      return !value || !!value.match(ALPHANUMERIC_HYPHEN_ONLY_RX);
    }
  };

  /**
   * Validation against name specific strings.
   * This includes spaces, comma, numeric.
   * @param context field context */
  public alphaNumericOnly = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;
    if (typeof value === 'string') {
      return !value || !!value.match(NAME_SPECIFIC_CHARACTERS_NUMERIC_ONLY_RX);
    }
  };

  public jointCreditRequired = (context: LogicFnContext, hasJoint: boolean): boolean => {
    if (hasJoint) {
      return new JointCreditManager(this.statefulService, PROTOCOL_MORTGAGE).validation.validateJointCreditRequired;
    }
    return true;
  };

  public duplicatedApplicantSelection = (context: LogicFnContext, hasJoint: boolean): boolean => {
    if (hasJoint) {
      return new JointCreditManager(this.statefulService, PROTOCOL_MORTGAGE).validation.validationDuplicateSelection;
    }
    return true;
  };

  /**
   * validation against Beneficiary Contingent
   * @param context field context
   */
  public validateBeneficiary = (context: LogicFnContext, path: string, value: string): boolean => {
    const storageId = path;
    const arrayRoot = storageId.split('/$')[0].split('$')[0];
    const arraySize = this.statefulService.arraySize(arrayRoot);
    for (let i = 0; i < arraySize; i++) {
      const valueFromArray = this.statefulService.wrap(path, i).valueString;
      if (valueFromArray && value.toLowerCase() === valueFromArray.toLowerCase()) {
        return true;
      }
    }

    if (context.field?.data.value) {
      context.field.dirty = true;
    }

    return false;
  };

  /** Required Validation for Eligibility Questions */
  public validateEligibilityQuestions = (context: LogicFnContext, path: string, iterationNum = 0): boolean => {
    const storageId = path;
    const arrayRoot = storageId.split('/$')[0].split('$')[0];
    const arraySize = this.statefulService.arraySize(arrayRoot);

    if (arraySize > iterationNum) {
      const value = this.statefulService.wrap(path, iterationNum).valueString;
      return !!value;
    }
    return true;
  };

  /**
   *  Show validation error message when total percentage is > 100
   * @param context field context
   * @param oaoBusinessRootId field string
   * @param oaoBusinessPercentageId field String
   */
  public validatePercentageAllocation = (
    context: LogicFnContext,
    oaoBusinessRootId: string,
    oaoBusinessPercentageId: string,
    oaoAdditionalRootId: string,
    oaoAdditionalpercentageId: string
  ): boolean => {
    let totalPercentage = 0;
    totalPercentage +=
      this.getPercentage(oaoBusinessRootId, oaoBusinessPercentageId) + this.getPercentage(oaoAdditionalRootId, oaoAdditionalpercentageId);
    if (totalPercentage <= MAX_PERCENT) {
      return true;
    }
    return false;
  };
  private readonly getPercentage = (oaoBusinessRootId: string, oaoBusinessPercentageId: string) => {
    let totalPercentage = 0;
    if (oaoBusinessRootId && oaoBusinessPercentageId) {
      const responsibleCardSize = this.statefulService.arraySize(oaoBusinessRootId);
      for (let i = 0; i < responsibleCardSize; i++) {
        const val = this.statefulService.wrap(oaoBusinessPercentageId, i).valueNumberOnly;
        totalPercentage += val || 0;
      }
    }
    return totalPercentage;
  };

  /**
   * validation if field is required
   * @param context field context
   * @param relatedFieldName field to compare if required
   */
  public isRequired = (context: LogicFnContext, relatedFieldName: string): boolean => {
    return relatedFieldName !== null;
  };

  /**
   * Check Consent Agreed or not
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isConsentAgreed = (context: LogicFnContext): boolean => {
    const disclosureCheck = this.statefulService.wrap('api:oaoMemberInformation/isConsentAgreed').value;
    return disclosureCheck === true;
  };

  /**
   * validation if field is required
   * @param context field context
   */
  public shortdate = (context: LogicFnContext): boolean => {
    const value = context.field.data.value as string;
    if (!value) {
      return false;
    }

    const mmyyyyArray = value.split('/');
    if (!mmyyyyArray || mmyyyyArray.length !== 2 || mmyyyyArray[0].length !== 2 || mmyyyyArray[1].length !== 4) {
      return false;
    }

    return true;
  };

  /**
   * Test for main applicant- who does not have a 1003 parent)
   */
  public isMainBorrowerOrPreviousAddress = (context: LogicFnContext): boolean => {
    return (
      context.field.iterationId > 0 ||
      !new JointCreditManager(this.statefulService, PROTOCOL_MORTGAGE).getMainApplicantByChildIndex(context.field.pageIterationId)
    );
  };

  /**
   * ssn validation for applicant and co-applicant
   */
  public uniqueSSN = (context: LogicFnContext, applicant: string): boolean => {
    // const value = context.field.data.value ? context.field.data.value.toString().replace(/-/g, '') : '';
    let coBorrowers = this.statefulService.arraySize(coBorrowersPath(PROTOCOL_MORTGAGE)) - 1;
    const ssnData = this.statefulService.wrap(
      `${coBorrowersPath(PROTOCOL_MORTGAGE)}/#/borrowerInfo/ssn`,
      null,
      context.field.pageIterationId
    );
    const value = ssnData.value ? ssnData.value.toString().replace(/-/g, '') : '';

    if (applicant && value && coBorrowers >= 0) {
      const tempList = [];

      if (context.field.pageIterationId >= 0) {
        tempList.push(applicant);
      }
      while (coBorrowers >= 0) {
        if (coBorrowers !== context.field.pageIterationId) {
          const ssn = this.statefulService.wrap(`${coBorrowersPath(PROTOCOL_MORTGAGE)}/#/borrowerInfo/ssn`, null, coBorrowers);
          if (ssn?.value) {
            tempList.push(ssn.value.toString().replace(/-/g, ''));
          }
        }
        coBorrowers--;
      }

      if (tempList.includes(value.toString())) {
        return false;
      }
    }

    return true;
  };

  uniqueMobile = (context: LogicFnContext, primaryStoragePath: string, mobile: string): boolean => {
    if (primaryStoragePath === mobile) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * isNotNullAndEmpty
   * @param context field context
   * @returns true if it is not null or empty.
   */
  public isNotNullAndEmpty = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;

    if (!value) {
      return false;
    }

    if (typeof value === 'string') {
      const trimedValue = value.trim();
      if (trimedValue.length === 0) {
        return false;
      }
    }
  };
}
